import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";
import { Check, fetchCheckList } from "../../api/check";
import { Category, createCategory, fetchCategoryById, updateCategory } from "../../api/category";
import { useNotification } from "../Notification/NotificationProvider";
import { ModalPortal } from "../ModalPortal/ModalPortal";
import { CircularProgress } from "../CircularProgress/CircularProgress";
import {
  CategoryFormValues,
  categoryToForm,
  emptyCategoryForm,
  formToCategoryCreate,
  formToCategoryUpdate,
  mergeCategoryCheckOptions,
  validateCategoryForm,
} from "./categoryForm";
import "./CategoryModal.scss";

interface CategoryModalProps {
  categoryId?: string | null;
  onClose: () => void;
  onSaved: () => void;
}

const getCheckLabel = (check: Check): string =>
  check.description?.trim() || check.code?.trim() || check.id || "—";

const getCheckSubtitle = (check: Check): string | null => {
  const title = getCheckLabel(check);
  const code = check.code?.trim();
  if (!code || code === title) return null;
  return code;
};

export const CategoryModal = ({ categoryId, onClose, onSaved }: CategoryModalProps) => {
  const { showNotification } = useNotification();
  const isCreate = !categoryId;
  const [loading, setLoading] = useState(!isCreate);
  const [checksLoading, setChecksLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(isCreate);
  const [form, setForm] = useState<CategoryFormValues>(emptyCategoryForm);
  const [checks, setChecks] = useState<Check[]>([]);
  const [loadedCategory, setLoadedCategory] = useState<Category | null>(null);

  useEffect(() => {
    let cancelled = false;
    setChecksLoading(true);

    fetchCheckList()
      .then((items) => {
        if (!cancelled) {
          setChecks(items.filter((check) => check.id));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setChecks([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setChecksLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!categoryId) {
      setForm(emptyCategoryForm());
      setLoadedCategory(null);
      setIsEditing(true);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setIsEditing(false);

    fetchCategoryById(categoryId)
      .then((item) => {
        if (!cancelled) {
          setLoadedCategory(item);
          setForm(categoryToForm(item));
        }
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [categoryId, showNotification]);

  const readOnly = !isEditing;

  const displayChecks = useMemo(
    () => mergeCategoryCheckOptions(checks, loadedCategory),
    [checks, loadedCategory]
  );

  const selectedChecks = useMemo(
    () => displayChecks.filter((check) => check.id && form.checkIds.includes(check.id)),
    [displayChecks, form.checkIds]
  );

  const toggleCheck = useCallback((checkId: string) => {
    setForm((current) => {
      const exists = current.checkIds.includes(checkId);
      return {
        ...current,
        checkIds: exists
          ? current.checkIds.filter((id) => id !== checkId)
          : [...current.checkIds, checkId],
      };
    });
  }, []);

  const handleSubmit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      const validationError = validateCategoryForm(form);
      if (validationError) {
        showNotification({ message: validationError, variant: "error" });
        return;
      }

      setSaving(true);

      try {
        if (isCreate) {
          await createCategory(formToCategoryCreate(form));
          showNotification({ message: "Категория создана", variant: "success" });
        } else if (categoryId) {
          await updateCategory(categoryId, formToCategoryUpdate(form, categoryId));
          showNotification({ message: "Категория обновлена", variant: "success" });
        }
        onSaved();
        onClose();
      } catch (err: unknown) {
      } finally {
        setSaving(false);
      }
    },
    [form, isCreate, categoryId, onClose, onSaved, showNotification]
  );

  const title = isCreate ? "Создать категорию" : isEditing ? "Редактировать категорию" : "Категория";

  return (
    <ModalPortal>
      <div className="detail-checks-modal-overlay" onClick={onClose}>
        <div
          className="detail-checks-modal detail-checks-modal--category"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="detail-checks-modal-head">
            <h3>{title}</h3>
            <button type="button" className="detail-tasklist-close" onClick={onClose} aria-label="Закрыть">
              <X size={18} />
            </button>
          </div>

          {loading ? (
            <div className="category-modal-loading">
              <CircularProgress size="lg" />
            </div>
          ) : (
            <>
              <form
                id="category-modal-form"
                className="detail-checks-modal-body create-request-modal-form category-modal-form"
                onSubmit={handleSubmit}
              >
              <div className="create-request-modal-form-group">
                <label className="create-request-modal-label">Название</label>
                <input
                  type="text"
                  className="create-request-modal-input"
                  value={form.name}
                  disabled={readOnly}
                  autoFocus={isEditing}
                  onChange={(e) => setForm((current) => ({ ...current, name: e.target.value }))}
                />
              </div>

              <div className="create-request-modal-form-group">
                <label className="create-request-modal-label">Описание</label>
                <textarea
                  className="create-request-modal-input category-modal-textarea"
                  value={form.description}
                  disabled={readOnly}
                  rows={3}
                  onChange={(e) =>
                    setForm((current) => ({ ...current, description: e.target.value }))
                  }
                />
              </div>

              <div className="category-modal-checks">
                <div className="category-modal-checks-head">
                  <span className="create-request-modal-label">Проверки</span>
                  {isEditing && (
                    <span className="category-modal-checks-hint">
                      Отметьте проверки, чтобы добавить в категорию, снимите — чтобы исключить
                    </span>
                  )}
                </div>
                <div className="category-modal-checks-list">
                  {checksLoading ? (
                    <div className="category-modal-checks-loading">
                      <CircularProgress size="md" />
                    </div>
                  ) : readOnly ? (
                    selectedChecks.length === 0 ? (
                      <p className="category-modal-checks-empty">Проверки не выбраны</p>
                    ) : (
                      <ul className="category-modal-selected-checks">
                        {selectedChecks.map((check) => (
                          <li key={check.id} className="category-modal-selected-check-item">
                            <span className="category-modal-check-title">{getCheckLabel(check)}</span>
                            {getCheckSubtitle(check) && (
                              <span className="category-modal-check-code">{getCheckSubtitle(check)}</span>
                            )}
                          </li>
                        ))}
                      </ul>
                    )
                  ) : displayChecks.length === 0 ? (
                    <p className="category-modal-checks-empty">Нет доступных проверок</p>
                  ) : (
                    displayChecks.map((check) => {
                      const checkId = check.id as string;
                      const selected = form.checkIds.includes(checkId);
                      const subtitle = getCheckSubtitle(check);

                      return (
                        <label
                          key={checkId}
                          className={`category-modal-check-item${
                            selected ? " category-modal-check-item--selected" : ""
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={() => toggleCheck(checkId)}
                          />
                          <span className="category-modal-check-text">
                            <span className="category-modal-check-title">{getCheckLabel(check)}</span>
                            {subtitle && (
                              <span className="category-modal-check-code">{subtitle}</span>
                            )}
                          </span>
                        </label>
                      );
                    })
                  )}
                </div>
              </div>

              </form>

              <div className="detail-checks-modal-footer category-modal-actions">
                {!isCreate && !isEditing && (
                  <button
                    type="button"
                    className="category-modal-secondary-btn"
                    onClick={() => setIsEditing(true)}
                  >
                    Редактировать
                  </button>
                )}
                {!isCreate && isEditing && (
                  <button
                    type="button"
                    className="category-modal-secondary-btn"
                    disabled={saving}
                    onClick={() => {
                      setIsEditing(false);
                      if (categoryId) {
                        setLoading(true);
                        fetchCategoryById(categoryId)
                          .then((item) => {
                            setLoadedCategory(item);
                            setForm(categoryToForm(item));
                          })
                          .finally(() => setLoading(false));
                      }
                    }}
                  >
                    Отмена
                  </button>
                )}
                {(isCreate || isEditing) && (
                  <button
                    type="submit"
                    form="category-modal-form"
                    className="create-request-modal-submit"
                    disabled={saving}
                  >
                    {saving ? <CircularProgress size="sm" /> : isCreate ? "Создать" : "Сохранить"}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </ModalPortal>
  );
};
